/**
 * Student Service
 *
 * Handles student account management for SciQuest Heroes.
 * Students use custom username/password authentication (no Supabase Auth).
 */

import { getSupabaseClient, hasSupabaseConfig } from './supabase-client';
import { callEdgeFunction } from './edge-function-client';
import type { Student, StudentSignupData, StudentLoginCredentials } from '../utils/types';
import { userCache, generateCacheKey } from '@utils/cache-helper';
import { hashPassword } from '@utils/password-helper';
import { setStudentSession, getStudentSession, clearStudentSession } from '@utils/session-helper';
import { posthogCapture, posthogReset } from './posthog-service';

export class StudentService {
  /**
   * Generate a student password (3 capitals + 4 digits + 2 lowercase)
   * Example: ABC1234xy
   */
  static generateStudentPassword(): string {
    const capitals = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const digits = '0123456789';

    let password = '';

    // 3 capital letters
    for (let i = 0; i < 3; i++) {
      password += capitals.charAt(Math.floor(Math.random() * capitals.length));
    }

    // 4 digits
    for (let i = 0; i < 4; i++) {
      password += digits.charAt(Math.floor(Math.random() * digits.length));
    }

    // 2 lowercase letters
    for (let i = 0; i < 2; i++) {
      password += lowercase.charAt(Math.floor(Math.random() * lowercase.length));
    }

    return password;
  }

  /**
   * Generate a unique user ID for students
   * Format: 3 uppercase letters + 3 digits (e.g., ABC123)
   * Short, easy to remember, and kid-friendly
   */
  static generateStudentUserId(): string {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digits = '0123456789';

    let userId = '';

    // 3 uppercase letters
    for (let i = 0; i < 3; i++) {
      userId += letters.charAt(Math.floor(Math.random() * letters.length));
    }

    // 3 digits
    for (let i = 0; i < 3; i++) {
      userId += digits.charAt(Math.floor(Math.random() * digits.length));
    }

    return userId;
  }

  /**
   * Create a student account (called by guardian)
   */
  static async createStudent(
    studentData: StudentSignupData
  ): Promise<{ student: Student | null; userId: string | null; password: string | null; error: Error | null }> {
    try {
      if (!hasSupabaseConfig()) {
        // Mock mode
        const userId = this.generateStudentUserId();
        const password = this.generateStudentPassword();
        const mockStudent: Student = {
          id: `student-${Date.now()}`,
          created_at: new Date().toISOString(),
          user_id: userId,
          password: await hashPassword(password),
          full_name: studentData.full_name,
          age: studentData.age,
          grade: studentData.grade,
          avatar: studentData.avatar,
          guardian_id: studentData.guardian_id,
        };
        return { student: mockStudent, userId, password, error: null };
      }

      const supabase = getSupabaseClient();

      // Generate credentials for the student
      const userId = this.generateStudentUserId();
      const password = this.generateStudentPassword();

      // Hash the password before storing
      const hashedPassword = await hashPassword(password);

      // Create student record directly in students table
      const { data: studentRecord, error: studentError } = await supabase
        .from('students')
        .insert({
          user_id: userId,
          password: hashedPassword,
          full_name: studentData.full_name,
          age: studentData.age,
          grade: studentData.grade,
          avatar: studentData.avatar,
          guardian_id: studentData.guardian_id,
        })
        .select()
        .single();

      if (studentError) {
        return { student: null, userId: null, password: null, error: studentError };
      }

      // Invalidate guardian's student list cache
      const guardianCacheKey = generateCacheKey('students_guardian', studentData.guardian_id);
      userCache.clear(guardianCacheKey);

      // Attributed to the guardian (who is logged in and identified in PostHog).
      // No student PII — grade is safe, age is omitted per COPPA.
      posthogCapture('student created', { grade: studentData.grade });
      return { student: studentRecord as Student, userId, password, error: null };
    } catch (error) {
      return { student: null, userId: null, password: null, error: error as Error };
    }
  }

  /**
   * Login student with user_id and password (custom authentication)
   */
  static async loginStudent(
    credentials: StudentLoginCredentials
  ): Promise<{ student: Student | null; error: Error | null }> {
    try {
      if (!hasSupabaseConfig()) {
        const mockStudent: Student = {
          id: 'mock-student-123',
          created_at: new Date().toISOString(),
          user_id: credentials.user_id,
          password: await hashPassword(credentials.password),
          full_name: 'Mock Student',
          guardian_id: 'mock-guardian-123',
        };
        // Store session with timestamp
        setStudentSession(mockStudent);
        return { student: mockStudent, error: null };
      }

      // Call Edge Function for secure authentication (service role key stays server-side)
      const result = await callEdgeFunction<{ student: Student; token: string }>(
        'student-auth',
        'login',
        { user_id: credentials.user_id, password: credentials.password },
        false // login doesn't require existing auth
      );

      const student = result.student;

      // Store student session with JWT token
      setStudentSession(student, result.token);

      // Reset any active guardian identity so student events are never linked to a guardian profile (COPPA).
      posthogReset();
      // Capture without identify — students are not profiled in PostHog (COPPA).
      posthogCapture('student logged in', { grade: student.grade });
      return { student, error: null };
    } catch (error) {
      return { student: null, error: error as Error };
    }
  }

  /**
   * Get students for a guardian (with caching)
   */
  static async getStudentsByGuardian(guardianId: string): Promise<{ students: Student[]; error: Error | null }> {
    try {
      // Check cache first
      const cacheKey = generateCacheKey('students_guardian', guardianId);
      const cached = userCache.get(cacheKey);
      if (cached) {
        return { students: cached as Student[], error: null };
      }

      if (!hasSupabaseConfig()) {
        const mockStudents: Student[] = [
          {
            id: 'student-1',
            created_at: new Date().toISOString(),
            user_id: 'ABC123',
            password: 'hashed',
            full_name: 'Mock Student 1',
            age: 10,
            grade: '5',
            avatar: '👦',
            guardian_id: guardianId,
          },
        ];
        userCache.set(cacheKey, mockStudents);
        return { students: mockStudents, error: null };
      }

      const supabase = getSupabaseClient();
      const { data, error } = await supabase.from('students').select('*').eq('guardian_id', guardianId);

      if (error) {
        return { students: [], error };
      }

      const students = data as Student[];
      // Cache the result
      userCache.set(cacheKey, students);
      return { students, error: null };
    } catch (error) {
      return { students: [], error: error as Error };
    }
  }

  /**
   * Get student by ID
   */
  static async getStudentById(studentId: string): Promise<{ student: Student | null; error: Error | null }> {
    try {
      if (!hasSupabaseConfig()) {
        const mockStudent: Student = {
          id: studentId,
          created_at: new Date().toISOString(),
          user_id: 'ABC123',
          password: 'hashed',
          full_name: 'Mock Student',
          age: 10,
          grade: '5',
          avatar: '👦',
          guardian_id: 'mock-guardian-123',
        };
        return { student: mockStudent, error: null };
      }

      // Call Edge Function for secure data access (service role key stays server-side)
      const result = await callEdgeFunction<{ student: Student }>(
        'student-auth',
        'get-profile',
        { studentId }
      );

      return { student: result.student, error: null };
    } catch (error) {
      return { student: null, error: error as Error };
    }
  }

  /**
   * Update student information (invalidates cache)
   */
  static async updateStudent(
    studentId: string,
    updates: Partial<Student>
  ): Promise<{ student: Student | null; error: Error | null }> {
    try {
      if (!hasSupabaseConfig()) {
        const mockStudent: Student = {
          id: studentId,
          created_at: new Date().toISOString(),
          user_id: 'ABC123',
          password: updates.password || 'hashed',
          full_name: updates.full_name || 'Mock Student',
          age: updates.age,
          grade: updates.grade,
          avatar: updates.avatar,
          guardian_id: updates.guardian_id || 'mock-guardian-123',
        };
        // Invalidate cache
        userCache.clear(generateCacheKey('students_guardian', mockStudent.guardian_id));
        return { student: mockStudent, error: null };
      }

      // Call Edge Function for secure update (password hashed server-side)
      const result = await callEdgeFunction<{ student: Student }>(
        'student-auth',
        'update-profile',
        { studentId, updates }
      );

      const student = result.student;
      // Invalidate guardian's student list cache
      userCache.clear(generateCacheKey('students_guardian', student.guardian_id));

      return { student, error: null };
    } catch (error) {
      return { student: null, error: error as Error };
    }
  }

  /**
   * Transfer student to another guardian
   */
  static async transferStudent(
    studentId: string,
    newGuardianId: string
  ): Promise<{ student: Student | null; error: Error | null }> {
    try {
      // Get current student to clear old guardian's cache
      const { student: currentStudent } = await this.getStudentById(studentId);

      if (currentStudent) {
        // Clear old guardian's cache
        userCache.clear(generateCacheKey('students_guardian', currentStudent.guardian_id));
      }

      // Update guardian_id
      const result = await this.updateStudent(studentId, {
        guardian_id: newGuardianId,
      });

      // Clear new guardian's cache
      userCache.clear(generateCacheKey('students_guardian', newGuardianId));

      return result;
    } catch (error) {
      return { student: null, error: error as Error };
    }
  }

  /**
   * Get current student from session (validates timeout)
   * Returns null if session is expired
   */
  static getCurrentStudent(): Student | null {
    return getStudentSession();
  }

  /**
   * Logout student (clear session)
   */
  static logout(): void {
    clearStudentSession();
  }

  /**
   * Redirect to student dashboard
   */
  static redirectToDashboard(): void {
    window.location.href = '/student/dashboard.html';
  }
}

export default StudentService;
